import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { ShoppingCart, User } from "lucide-react";

import { CartDrawer } from "@/components/shop/CartDrawer";
import { KioskKeyboardBoundary } from "@/components/shop/KioskKeyboardBoundary";
import { fetchBuyerMe, type BuyerProfile } from "@/features/buyerAuth";
import { imageUrl } from "@/features/imageUpload";
import { loadCart, saveCart } from "@/features/shopCart";
import { isKioskMode } from "@/lib/kioskMode";
import {
  fetchStorefrontFeed,
  fetchStorefrontProfile,
  type StorefrontCard,
  type StorefrontProfile,
} from "@/features/storefront";
// Self-hosted fonts via @fontsource — bundled by Vite, served same-origin,
// so they're safe under the API's `style-src 'self' 'unsafe-inline'` CSP
// that was blocking the previous Google Fonts @import (TNK-107). Loaded
// from shop.tsx so they're code-split with the /shop bundle — operator
// routes don't pay the bandwidth.
import "@fontsource/cinzel/400.css";
import "@fontsource/cinzel/600.css";
import "@fontsource/cinzel/700.css";
import "@fontsource/cinzel/900.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/700.css";
import "@/styles/shop.theme.css";

// Touchscreen-kiosk on-screen keyboard (TCGMA-176). Lazy so normal web visitors
// never download it — it only ever renders when ?kiosk=1 was on the entry URL.
const KioskKeyboard = lazy(() => import("@/components/shop/KioskKeyboard"));

export const Route = createFileRoute("/shop")({
  component: ShopLayout,
});

const ShopProfileContext = createContext<StorefrontProfile | null>(null);

interface CartValue {
  /** Card ids in the cart. */
  ids: string[];
  has: (id: string) => boolean;
  add: (id: string) => void;
  remove: (id: string) => void;
  /** TNK-212 phase 2b — locked-bundle product ids in the cart. */
  productIds: string[];
  hasProduct: (id: string) => boolean;
  addProduct: (id: string) => void;
  removeProduct: (id: string) => void;
  clear: () => void;
  /** Total line items (cards + bundles) — drives the header/badge counts. */
  count: number;
  /** Open the cart drawer. Exposed so child surfaces (e.g. the sticky
   *  search bar on `/shop/inventory`) can mount their own cart-icon
   *  shortcuts without re-implementing the drawer state. */
  openDrawer: () => void;
}
const CartContext = createContext<CartValue | null>(null);

// Inventory warm cache (TNK-106 phase 2, repurposed in TNK-124 phase G).
// Pre-warms the storefront's FIRST PAGE only — not the full inventory.
// Consumers that need a card by id route through `useStorefrontCardLookup`
// which falls back to `/api/store/cards/:id` when the id isn't in this
// page. At 50K cards the legacy single-fetch warm was ~42 MB / 14 s; the
// first-page warm is ~96 KB / 0.6 s.
export type ShopInventoryState =
  | { phase: "loading" }
  | { phase: "ready"; cards: StorefrontCard[] }
  | { phase: "error" };
const ShopInventoryContext = createContext<ShopInventoryState | null>(null);

// Size of the warm first-page fetch. Matches the default the inventory
// feed uses; if a buyer scrolls, the feed picks up from page 2 — no
// duplication.
const WARM_PAGE_SIZE = 120;

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; profile: StorefrontProfile }
  | { phase: "unavailable" }
  | { phase: "error" };

function ShopLayout() {
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [inventory, setInventory] = useState<ShopInventoryState>({
    phase: "loading",
  });
  const [cartOpen, setCartOpen] = useState(false);
  // Read once — the flag is fixed for the session (set from the entry URL in
  // main.tsx). Lazy initializer so we don't touch sessionStorage every render.
  const [kiosk] = useState(isKioskMode);

  const [cartIds, setCartIds] = useState<string[]>(() => loadCart().cardIds);
  const [cartProductIds, setCartProductIds] = useState<string[]>(
    () => loadCart().productIds,
  );
  useEffect(() => {
    saveCart({ cardIds: cartIds, productIds: cartProductIds });
  }, [cartIds, cartProductIds]);

  // TNK-202 — nav label reflects auth state ("Sign in / Register" vs "Account").
  // undefined = /buyer/me in flight; treated as not-signed-in for the label.
  const [buyer, setBuyer] = useState<BuyerProfile | null | undefined>(
    undefined,
  );
  useEffect(() => {
    void fetchBuyerMe().then(setBuyer);
  }, []);
  const cart = useMemo<CartValue>(
    () => ({
      ids: cartIds,
      has: (id) => cartIds.includes(id),
      add: (id) =>
        setCartIds((prev) => (prev.includes(id) ? prev : [...prev, id])),
      remove: (id) => setCartIds((prev) => prev.filter((x) => x !== id)),
      productIds: cartProductIds,
      hasProduct: (id) => cartProductIds.includes(id),
      addProduct: (id) =>
        setCartProductIds((prev) => (prev.includes(id) ? prev : [...prev, id])),
      removeProduct: (id) =>
        setCartProductIds((prev) => prev.filter((x) => x !== id)),
      clear: () => {
        setCartIds([]);
        setCartProductIds([]);
      },
      count: cartIds.length + cartProductIds.length,
      openDrawer: () => setCartOpen(true),
    }),
    [cartIds, cartProductIds],
  );

  // Fetch profile + warm first inventory page in parallel from the
  // start. If the profile resolves "unavailable" we'll never render
  // the children that consume the inventory anyway, so a wasted fetch
  // in that case is acceptable for the faster cold-start on the
  // common case.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [profileRes, inventoryRes] = await Promise.allSettled([
        fetchStorefrontProfile(),
        fetchStorefrontFeed({ limit: WARM_PAGE_SIZE }),
      ]);
      if (cancelled) return;
      if (profileRes.status === "fulfilled") {
        setState(
          profileRes.value
            ? { phase: "ready", profile: profileRes.value }
            : { phase: "unavailable" },
        );
      } else {
        setState({ phase: "error" });
      }
      if (inventoryRes.status === "fulfilled") {
        setInventory({
          phase: "ready",
          cards: inventoryRes.value.cards,
        });
      } else {
        setInventory({ phase: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.phase !== "ready") {
    return (
      <div className="shop-theme flex min-h-screen items-center justify-center bg-background px-6 text-center text-muted-foreground">
        {state.phase === "loading"
          ? "Loading store…"
          : state.phase === "unavailable"
            ? "This store isn’t open right now."
            : "Couldn’t load the store. Please try again later."}
      </div>
    );
  }

  const { profile } = state;
  const logo = profile.logoRef ? imageUrl(profile.logoRef) : null;

  return (
    <div className="shop-theme flex min-h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/shop" className="flex items-center gap-2.5">
            {logo && (
              <img src={logo} alt="" className="h-8 w-8 rounded object-cover" />
            )}
            <span className="text-base font-semibold text-foreground">
              {profile.storeName || "Card Store"}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/shop/inventory"
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              Browse cards
            </Link>
            {/* TNK-167 — return to the buyer account / reservations dashboard.
                Links to /shop/account, which renders the dashboard when signed
                in and a sign-in prompt when not, so one button serves both. */}
            <Link
              to="/shop/account"
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              <User className="h-4 w-4" />
              {buyer ? "Account" : "Sign in / Register"}
            </Link>
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="relative inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
              aria-label={`Open cart (${cart.count} item${cart.count === 1 ? "" : "s"})`}
            >
              <ShoppingCart className="h-4 w-4" />
              {cart.count > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">
                  {cart.count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <ShopProfileContext.Provider value={profile}>
        <ShopInventoryContext.Provider value={inventory}>
          <CartContext.Provider value={cart}>
            <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
              <Outlet />
            </main>

            <footer className="border-t border-border px-4 py-4 text-center text-[11px] text-muted-foreground">
              <p>
                {profile.storeName || "Card Store"} · Reservations are requests,
                not purchases — the seller confirms availability.
              </p>
              <p className="mt-1.5">
                <Link
                  to="/shop/terms"
                  className="hover:text-foreground hover:underline"
                >
                  Terms of Service
                </Link>
                <span className="mx-1.5">·</span>
                <Link
                  to="/shop/privacy"
                  className="hover:text-foreground hover:underline"
                >
                  Privacy Policy
                </Link>
              </p>
            </footer>

            <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

            {/* Kiosk panel has no physical keyboard and the OS one cannot be
                driven reliably from Chromium-on-Linux, so the page owns it.
                The boundary is load-bearing: without it a failed chunk import
                is silent, and a silent dead keyboard on an unattended panel is
                a customer standing there unable to type. */}
            {kiosk && (
              <KioskKeyboardBoundary>
                <Suspense fallback={null}>
                  <KioskKeyboard />
                </Suspense>
              </KioskKeyboardBoundary>
            )}
          </CartContext.Provider>
        </ShopInventoryContext.Provider>
      </ShopProfileContext.Provider>
    </div>
  );
}

export function useShopProfile(): StorefrontProfile {
  const profile = useContext(ShopProfileContext);
  if (!profile) {
    throw new Error("useShopProfile must be used within the /shop layout");
  }
  return profile;
}

export function useShopCart(): CartValue {
  const cart = useContext(CartContext);
  if (!cart) {
    throw new Error("useShopCart must be used within the /shop layout");
  }
  return cart;
}

export function useShopInventory(): ShopInventoryState {
  const inventory = useContext(ShopInventoryContext);
  if (!inventory) {
    throw new Error("useShopInventory must be used within the /shop layout");
  }
  return inventory;
}
